import React from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { COLORS, FONTS, SPRING, accentFor, SECTION_NS } from "./constants";
import {
  appearAt,
  BrandMark,
  countUp,
  Grad,
  glow,
  Pill,
  ramp,
  SlideChip,
  SlideShell,
} from "./shared";
import type { Slide } from "./slides";

/* ───────────────────────── shared bits ───────────────────────── */

const Heading: React.FC<{
  text: string;
  accent: string;
  appear: number;
  size?: number;
  top?: number;
}> = ({ text, accent, appear, size = 58, top = 150 }) => (
  <div
    style={{
      position: "absolute",
      top,
      left: 110,
      right: 110,
      opacity: appear,
      transform: `translateY(${(1 - appear) * -26}px)`,
      zIndex: 20,
    }}
  >
    <div
      style={{
        fontSize: size,
        fontFamily: FONTS.heading,
        fontWeight: 900,
        color: COLORS.textPrimary,
        letterSpacing: -1.2,
        lineHeight: 1.06,
        textShadow: `0 0 36px ${accent}aa, 0 0 80px ${accent}33`,
      }}
    >
      {text}
    </div>
  </div>
);

const Subhead: React.FC<{
  text: string;
  accent: string;
  appear: number;
  top: number;
}> = ({ text, accent, appear, top }) => (
  <div
    style={{
      position: "absolute",
      top,
      left: 110,
      right: 110,
      opacity: appear,
      fontSize: 27,
      fontFamily: FONTS.body,
      color: accent,
      fontWeight: 600,
      letterSpacing: 0.3,
      textShadow: glow(accent),
      zIndex: 20,
    }}
  >
    {text}
  </div>
);

/* ───────────────────────── flow parser (mermaid → labels) ───────────────────────── */

function parseFlow(src?: string): { nodes: string[]; loops: boolean } {
  if (!src) return { nodes: [], loops: false };
  // map every node id to its bracketed label, wherever defined
  const idLabel: Record<string, string> = {};
  const defRe = /([A-Za-z0-9_]+)\s*[[({]([^\])}]+)[\])}]/g;
  let mm: RegExpExecArray | null;
  while ((mm = defRe.exec(src)) !== null) idLabel[mm[1]] = mm[2].trim();

  const body = src.replace(/^\s*(graph|flowchart)\s+[A-Za-z]+;?/i, " ");
  const tokens = body
    .split(/-->|---|-\.->|==>|->/)
    .map((t) => t.trim().replace(/^\|[^|]*\|\s*/, ""))
    .filter(Boolean);

  const seq: string[] = [];
  for (const t of tokens) {
    const idm = t.match(/^[A-Za-z0-9_]+/);
    if (!idm) continue;
    const label = idLabel[idm[0]] || idm[0];
    if (seq[seq.length - 1] !== label) seq.push(label);
  }
  // a back-edge to the first node = a closed loop; drop the duplicate, flag it
  let loops = false;
  if (seq.length > 2 && seq[seq.length - 1] === seq[0]) {
    seq.pop();
    loops = true;
  }
  return { nodes: seq.slice(0, 8), loops };
}

/* ───────────────────────── layouts ───────────────────────── */

const TitleLayout: React.FC<{ slide: Slide; accent: string }> = ({
  slide,
  accent,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const a = appearAt(frame, fps, 6);
  const b = appearAt(frame, fps, 18);
  const c = appearAt(frame, fps, 32);
  return (
    <SlideShell accent={accent}>
      <BrandMark appear={a} />
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "0 130px",
        }}
      >
        <Pill
          text={slide.kicker || "EAKC"}
          color={accent}
          appear={a}
          size={20}
        />
        <div
          style={{
            marginTop: 26,
            fontSize: 96,
            lineHeight: 1.02,
            fontFamily: FONTS.heading,
            fontWeight: 900,
            letterSpacing: -3,
            maxWidth: 1500,
            opacity: b,
            transform: `translateY(${(1 - b) * 34}px)`,
            textShadow: `0 0 60px ${accent}66`,
          }}
        >
          <Grad from="#FFFFFF" to={accent}>
            {slide.title}
          </Grad>
        </div>
        {slide.subtitle ? (
          <div
            style={{
              marginTop: 26,
              fontSize: 32,
              fontFamily: FONTS.body,
              color: COLORS.textSecondary,
              maxWidth: 1180,
              lineHeight: 1.4,
              opacity: c,
            }}
          >
            {slide.subtitle}
          </div>
        ) : null}
        <div style={{ display: "flex", gap: 14, marginTop: 46, opacity: c }}>
          <Pill
            text={
              <span>
                <b style={{ color: accent }}>224</b> commits · 2 weeks
              </span>
            }
            color={COLORS.neonBlue}
            size={19}
          />
          <Pill
            text={
              <span>
                <b style={{ color: accent }}>80</b> slides
              </span>
            }
            color={COLORS.neonPurple}
            size={19}
          />
          <Pill
            text="EAKC × Claude convergence"
            color={COLORS.neonCyan}
            size={19}
          />
        </div>
      </div>
    </SlideShell>
  );
};

const SectionLayout: React.FC<{ slide: Slide; accent: string }> = ({
  slide,
  accent,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const a = appearAt(frame, fps, 4);
  const b = appearAt(frame, fps, 16);
  const line = ramp(frame, 10, 40);
  return (
    <SlideShell accent={accent}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "0 140px",
        }}
      >
        <div
          style={{
            fontSize: 240,
            fontFamily: FONTS.mono,
            fontWeight: 800,
            letterSpacing: -8,
            lineHeight: 0.8,
            color: "transparent",
            WebkitTextStroke: `2px ${accent}66`,
            opacity: a,
            transform: `translateX(${(1 - a) * -40}px)`,
          }}
        >
          §
        </div>
        <div
          style={{
            width: line * 260,
            height: 5,
            background: accent,
            boxShadow: glow(accent, true),
            borderRadius: 4,
            margin: "18px 0 26px",
          }}
        />
        <div
          style={{
            fontSize: 78,
            fontFamily: FONTS.heading,
            fontWeight: 900,
            letterSpacing: -2,
            maxWidth: 1400,
            lineHeight: 1.04,
            opacity: b,
            transform: `translateY(${(1 - b) * 28}px)`,
          }}
        >
          <Grad from="#FFFFFF" to={accent}>
            {slide.title}
          </Grad>
        </div>
        {slide.subtitle ? (
          <div
            style={{
              marginTop: 22,
              fontSize: 29,
              color: COLORS.textSecondary,
              fontFamily: FONTS.body,
              maxWidth: 1100,
              lineHeight: 1.4,
              opacity: appearAt(frame, fps, 28),
            }}
          >
            {slide.subtitle}
          </div>
        ) : null}
      </div>
    </SlideShell>
  );
};

const StatementLayout: React.FC<{ slide: Slide; accent: string }> = ({
  slide,
  accent,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const a = appearAt(frame, fps, 4);
  const b = appearAt(frame, fps, 16);
  const c = appearAt(frame, fps, 30);
  const blob = `${slide.subtitle || ""} ${slide.narration || ""}`;
  const isIntuition = /\bintuition\b/i.test(blob);

  // ── Variant 1: analogy / "Intuition" slides get a distinct amber card ──
  if (isIntuition) {
    const amber = COLORS.neonAmber;
    return (
      <SlideShell accent={accent}>
        <SlideChip n={slide.n} color={accent} appear={a} label={slide.kicker} />
        <div
          style={{
            position: "absolute",
            right: 80,
            bottom: -40,
            fontSize: 460,
            opacity: 0.06 * a,
            filter: `drop-shadow(0 0 60px ${amber})`,
          }}
        >
          💡
        </div>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            padding: "0 150px",
          }}
        >
          <div
            style={{
              opacity: a,
              transform: `translateY(${(1 - a) * 18}px)`,
              marginBottom: 26,
            }}
          >
            <Pill text="✦ Intuition" color={amber} size={20} />
          </div>
          <div
            style={{
              fontSize: 60,
              fontFamily: FONTS.heading,
              fontWeight: 850,
              letterSpacing: -1.4,
              lineHeight: 1.1,
              maxWidth: 1400,
              color: COLORS.textPrimary,
              textShadow: `0 0 40px ${amber}55`,
              opacity: b,
              transform: `translateY(${(1 - b) * 26}px)`,
            }}
          >
            {slide.title}
          </div>
          {slide.subtitle ? (
            <div
              style={{
                marginTop: 26,
                fontSize: 31,
                color: amber,
                fontFamily: FONTS.body,
                fontWeight: 600,
                maxWidth: 1180,
                lineHeight: 1.4,
                opacity: c,
              }}
            >
              {slide.subtitle}
            </div>
          ) : null}
        </div>
      </SlideShell>
    );
  }

  // ── Variants 2 & 3: alternate centered / left-aligned for rhythm ──
  const centered = slide.n % 2 === 1;
  return (
    <SlideShell accent={accent}>
      <SlideChip n={slide.n} color={accent} appear={a} label={slide.kicker} />
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: centered ? "center" : "flex-start",
          textAlign: centered ? "center" : "left",
          padding: centered ? "0 200px" : "0 150px",
        }}
      >
        <div
          style={{
            width: centered ? 90 : 70,
            height: 6,
            background: accent,
            borderRadius: 4,
            boxShadow: glow(accent, true),
            opacity: a,
            marginBottom: 30,
          }}
        />
        <div
          style={{
            fontSize: centered ? 66 : 62,
            fontFamily: FONTS.heading,
            fontWeight: 850,
            letterSpacing: -1.5,
            lineHeight: 1.1,
            maxWidth: centered ? 1500 : 1480,
            color: COLORS.textPrimary,
            opacity: b,
            transform: `translateY(${(1 - b) * 28}px)`,
          }}
        >
          {slide.title}
        </div>
        {slide.subtitle ? (
          <div
            style={{
              marginTop: 28,
              fontSize: 30,
              color: COLORS.textSecondary,
              fontFamily: FONTS.body,
              maxWidth: centered ? 1100 : 1280,
              lineHeight: 1.45,
              opacity: c,
            }}
          >
            {slide.subtitle}
          </div>
        ) : null}
      </div>
    </SlideShell>
  );
};

const QuoteLayout: React.FC<{ slide: Slide; accent: string }> = ({
  slide,
  accent,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const a = appearAt(frame, fps, 4);
  const b = appearAt(frame, fps, 18);
  return (
    <SlideShell accent={accent}>
      <SlideChip n={slide.n} color={accent} appear={a} label={slide.kicker} />
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "0 200px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 160,
            lineHeight: 0.6,
            color: accent,
            opacity: a * 0.5,
            fontFamily: FONTS.heading,
          }}
        >
          “
        </div>
        <div
          style={{
            fontSize: 54,
            fontFamily: FONTS.heading,
            fontWeight: 700,
            fontStyle: "italic",
            lineHeight: 1.2,
            maxWidth: 1400,
            opacity: b,
            transform: `translateY(${(1 - b) * 24}px)`,
          }}
        >
          {slide.title}
        </div>
        {slide.subtitle ? (
          <div
            style={{
              marginTop: 30,
              fontSize: 26,
              color: accent,
              fontFamily: FONTS.mono,
              letterSpacing: 1,
              opacity: appearAt(frame, fps, 34),
            }}
          >
            — {slide.subtitle}
          </div>
        ) : null}
      </div>
    </SlideShell>
  );
};

const BulletsLayout: React.FC<{ slide: Slide; accent: string }> = ({
  slide,
  accent,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const a = appearAt(frame, fps, 4);
  const items = slide.bullets || [];
  const twoCol = items.length > 4;
  return (
    <SlideShell accent={accent}>
      <SlideChip n={slide.n} color={accent} appear={a} label={slide.kicker} />
      <Heading
        text={slide.title}
        accent={accent}
        appear={appearAt(frame, fps, 8)}
      />
      {slide.subtitle ? (
        <Subhead
          text={slide.subtitle}
          accent={accent}
          appear={appearAt(frame, fps, 16)}
          top={222}
        />
      ) : null}
      <div
        style={{
          position: "absolute",
          top: slide.subtitle ? 300 : 280,
          left: 110,
          right: 110,
          bottom: 90,
          display: "grid",
          gridTemplateColumns: twoCol ? "1fr 1fr" : "1fr",
          gap: 20,
          alignContent: "center",
        }}
      >
        {items.map((b, i) => {
          const sp = appearAt(frame, fps, 22 + i * 7, SPRING.gentle);
          return (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 20,
                alignItems: "flex-start",
                background: `linear-gradient(135deg, ${accent}14, ${COLORS.bgCard}e6)`,
                border: `1.5px solid ${accent}55`,
                borderRadius: 18,
                padding: "22px 26px",
                opacity: sp,
                transform: `translateX(${(1 - sp) * -40}px)`,
                boxShadow: `0 14px 34px rgba(0,0,0,0.45)`,
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  marginTop: 9,
                  background: `linear-gradient(135deg, ${accent}, ${COLORS.neonCyan})`,
                  boxShadow: glow(accent, true),
                }}
              />
              <div>
                <div
                  style={{
                    fontSize: 27,
                    fontFamily: FONTS.heading,
                    fontWeight: 750,
                    letterSpacing: -0.4,
                    color: COLORS.textPrimary,
                  }}
                >
                  {b.head}
                </div>
                {b.sub ? (
                  <div
                    style={{
                      marginTop: 5,
                      fontSize: 20,
                      fontFamily: FONTS.body,
                      color: COLORS.textSecondary,
                      lineHeight: 1.4,
                    }}
                  >
                    {b.sub}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </SlideShell>
  );
};

const MetricLayout: React.FC<{ slide: Slide; accent: string }> = ({
  slide,
  accent,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const a = appearAt(frame, fps, 4);
  const metrics = slide.metrics || [];
  return (
    <SlideShell accent={accent}>
      <SlideChip n={slide.n} color={accent} appear={a} label={slide.kicker} />
      <Heading
        text={slide.title}
        accent={accent}
        appear={appearAt(frame, fps, 8)}
      />
      {slide.subtitle ? (
        <Subhead
          text={slide.subtitle}
          accent={accent}
          appear={appearAt(frame, fps, 16)}
          top={222}
        />
      ) : null}
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 110,
          right: 110,
          display: "flex",
          gap: 28,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {metrics.map((m, i) => {
          const sp = appearAt(frame, fps, 24 + i * 9, SPRING.bouncy);
          const isInt = /^\d+$/.test(m.value.trim());
          const shown = isInt
            ? String(countUp(frame - (24 + i * 9), parseInt(m.value, 10), 30))
            : m.value;
          return (
            <div
              key={i}
              style={{
                flex: "1 1 0",
                maxWidth: 460,
                background: `linear-gradient(150deg, ${accent}1e, ${COLORS.bgCard}f2)`,
                border: `2px solid ${accent}`,
                borderRadius: 26,
                padding: "44px 36px",
                opacity: sp,
                transform: `translateY(${(1 - sp) * 40}px) scale(${0.9 + sp * 0.1})`,
                boxShadow: `0 0 48px ${accent}44, 0 24px 50px rgba(0,0,0,0.5)`,
              }}
            >
              <div
                style={{
                  fontSize: 110,
                  fontFamily: FONTS.heading,
                  fontWeight: 900,
                  letterSpacing: -4,
                  lineHeight: 0.95,
                  textShadow: `0 0 40px ${accent}aa`,
                }}
              >
                <Grad from="#FFFFFF" to={accent}>
                  {shown}
                </Grad>
              </div>
              <div
                style={{
                  marginTop: 16,
                  fontSize: 23,
                  fontFamily: FONTS.body,
                  color: COLORS.textSecondary,
                  lineHeight: 1.35,
                }}
              >
                {m.label}
              </div>
            </div>
          );
        })}
      </div>
    </SlideShell>
  );
};

const CompareLayout: React.FC<{ slide: Slide; accent: string }> = ({
  slide,
  accent,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const a = appearAt(frame, fps, 4);
  const rows = slide.compareRows || [];
  const eC = COLORS.neonCyan;
  const cC = COLORS.neonPurple;
  return (
    <SlideShell accent={accent}>
      <SlideChip n={slide.n} color={accent} appear={a} label={slide.kicker} />
      <Heading
        text={slide.title}
        accent={accent}
        appear={appearAt(frame, fps, 8)}
      />
      <div
        style={{
          position: "absolute",
          top: 268,
          left: 110,
          right: 110,
          bottom: 80,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 18,
        }}
      >
        <div
          style={{
            display: "flex",
            opacity: appearAt(frame, fps, 14),
            paddingLeft: 8,
          }}
        >
          <div
            style={{
              flex: 1,
              fontFamily: FONTS.mono,
              fontSize: 17,
              letterSpacing: 3,
              color: eC,
              fontWeight: 800,
            }}
          >
            EAKC
          </div>
          <div style={{ width: 70 }} />
          <div
            style={{
              flex: 1,
              fontFamily: FONTS.mono,
              fontSize: 17,
              letterSpacing: 3,
              color: cC,
              fontWeight: 800,
            }}
          >
            CLAUDE · ANTHROPIC
          </div>
        </div>
        {rows.map((r, i) => {
          const sp = appearAt(frame, fps, 22 + i * 8, SPRING.gentle);
          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "stretch",
                opacity: sp,
                transform: `translateY(${(1 - sp) * 22}px)`,
              }}
            >
              <div
                style={{
                  flex: 1,
                  background: `linear-gradient(135deg, ${eC}1a, ${COLORS.bgCard}f0)`,
                  border: `1.5px solid ${eC}66`,
                  borderRadius: "16px 0 0 16px",
                  padding: "20px 24px",
                  fontSize: 23,
                  fontFamily: FONTS.body,
                  fontWeight: 600,
                  color: COLORS.textPrimary,
                  lineHeight: 1.32,
                }}
              >
                {r.eakc}
              </div>
              <div
                style={{
                  width: 70,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: FONTS.mono,
                  fontSize: 30,
                  color: accent,
                  textShadow: glow(accent),
                }}
              >
                ≈
              </div>
              <div
                style={{
                  flex: 1,
                  background: `linear-gradient(135deg, ${cC}16, ${COLORS.bgCard}f0)`,
                  border: `1.5px solid ${cC}66`,
                  borderRadius: "0 16px 16px 0",
                  padding: "20px 24px",
                  fontSize: 23,
                  fontFamily: FONTS.body,
                  color: COLORS.textSecondary,
                  lineHeight: 1.32,
                }}
              >
                {r.claude}
              </div>
            </div>
          );
        })}
      </div>
    </SlideShell>
  );
};

const DiagramLayout: React.FC<{ slide: Slide; accent: string }> = ({
  slide,
  accent,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const a = appearAt(frame, fps, 4);
  const { nodes, loops } = parseFlow(slide.mermaid);
  // fall back to bullets if the flow didn't parse
  if (nodes.length < 2) {
    return <BulletsLayout slide={slide} accent={accent} />;
  }
  const vertical = (slide.mermaid || "").match(/\b(TD|TB)\b/i) != null;
  const n = nodes.length;
  const many = !vertical && n >= 6;
  const nodeFont = vertical ? 25 : n >= 6 ? 19 : n === 5 ? 22 : 25;
  const nodePad = vertical ? "22px 40px" : many ? "18px 16px" : "28px 22px";
  const nodeMaxW = vertical ? 520 : many ? 200 : 240;
  const arrowFont = vertical ? 38 : many ? 26 : 36;
  const arrowMargin = vertical ? "6px 0" : many ? "0 3px" : "0 7px";
  const palette = [
    accent,
    COLORS.neonBlue,
    COLORS.neonCyan,
    COLORS.neonPurple,
    COLORS.neonGreen,
    COLORS.neonPink,
    COLORS.neonAmber,
  ];
  return (
    <SlideShell accent={accent}>
      <SlideChip n={slide.n} color={accent} appear={a} label={slide.kicker} />
      <Heading
        text={slide.title}
        accent={accent}
        appear={appearAt(frame, fps, 8)}
      />
      {slide.subtitle ? (
        <Subhead
          text={slide.subtitle}
          accent={accent}
          appear={appearAt(frame, fps, 16)}
          top={222}
        />
      ) : null}
      <div
        style={{
          position: "absolute",
          top: 280,
          left: 90,
          right: 90,
          bottom: 90,
          display: "flex",
          flexDirection: vertical ? "column" : "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 0,
          flexWrap: "nowrap",
        }}
      >
        {nodes.map((label, i) => {
          const sp = appearAt(frame, fps, 22 + i * 10, SPRING.bouncy);
          const c = palette[i % palette.length];
          return (
            <React.Fragment key={i}>
              <div
                style={{
                  flexShrink: 0,
                  opacity: sp,
                  transform: `scale(${0.8 + sp * 0.2})`,
                  background: `linear-gradient(150deg, ${c}26, ${COLORS.bgCard}f2)`,
                  border: `2.5px solid ${c}`,
                  borderRadius: 20,
                  padding: nodePad,
                  maxWidth: nodeMaxW,
                  textAlign: "center",
                  fontSize: nodeFont,
                  fontFamily: FONTS.heading,
                  fontWeight: 750,
                  color: COLORS.textPrimary,
                  boxShadow: `0 0 36px ${c}55, 0 18px 38px rgba(0,0,0,0.5)`,
                  lineHeight: 1.2,
                }}
              >
                {label}
              </div>
              {i < nodes.length - 1 ? (
                <div
                  style={{
                    flexShrink: 0,
                    opacity: appearAt(frame, fps, 28 + i * 10),
                    color: accent,
                    fontSize: arrowFont,
                    margin: arrowMargin,
                    textShadow: glow(accent),
                    transform: vertical ? "rotate(90deg)" : "none",
                  }}
                >
                  ➜
                </div>
              ) : null}
            </React.Fragment>
          );
        })}
        {loops ? (
          <>
            <div
              style={{
                flexShrink: 0,
                opacity: appearAt(frame, fps, 28 + n * 10),
                color: accent,
                fontSize: arrowFont,
                margin: arrowMargin,
                textShadow: glow(accent),
                transform: vertical ? "rotate(90deg)" : "none",
              }}
            >
              ↻
            </div>
            <div
              style={{
                flexShrink: 0,
                opacity: appearAt(frame, fps, 32 + n * 10),
                border: `2px dashed ${accent}`,
                borderRadius: 999,
                padding: many ? "12px 18px" : "16px 22px",
                fontFamily: FONTS.mono,
                fontSize: many ? 14 : 16,
                fontWeight: 800,
                letterSpacing: 1,
                color: accent,
                textShadow: glow(accent),
                textAlign: "center",
                lineHeight: 1.25,
              }}
            >
              loops to
              <br />
              the start
            </div>
          </>
        ) : null}
      </div>
    </SlideShell>
  );
};

const SplitLayout: React.FC<{ slide: Slide; accent: string }> = ({
  slide,
  accent,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const a = appearAt(frame, fps, 4);
  const left = appearAt(frame, fps, 18, SPRING.gentle);
  const right = appearAt(frame, fps, 28, SPRING.gentle);
  const items = slide.bullets || [];
  return (
    <SlideShell accent={accent}>
      <SlideChip n={slide.n} color={accent} appear={a} label={slide.kicker} />
      <Heading
        text={slide.title}
        accent={accent}
        appear={appearAt(frame, fps, 8)}
      />
      <div
        style={{
          position: "absolute",
          top: 290,
          left: 110,
          right: 110,
          bottom: 90,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 40,
          alignItems: "center",
        }}
      >
        <div
          style={{
            opacity: left,
            transform: `translateX(${(1 - left) * -40}px)`,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {items.length ? (
            items.map((b, i) => (
              <div
                key={i}
                style={{ display: "flex", gap: 14, alignItems: "flex-start" }}
              >
                <div
                  style={{
                    flexShrink: 0,
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    marginTop: 9,
                    background: accent,
                    boxShadow: glow(accent),
                  }}
                />
                <div>
                  <div
                    style={{
                      fontSize: 25,
                      fontWeight: 700,
                      fontFamily: FONTS.heading,
                      color: COLORS.textPrimary,
                    }}
                  >
                    {b.head}
                  </div>
                  {b.sub ? (
                    <div
                      style={{
                        fontSize: 19,
                        color: COLORS.textSecondary,
                        lineHeight: 1.4,
                        marginTop: 4,
                      }}
                    >
                      {b.sub}
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          ) : (
            <div
              style={{
                fontSize: 30,
                color: COLORS.textSecondary,
                lineHeight: 1.5,
                fontFamily: FONTS.body,
              }}
            >
              {slide.subtitle}
            </div>
          )}
        </div>
        <div
          style={{
            opacity: right,
            transform: `translateX(${(1 - right) * 40}px) perspective(1200px) rotateY(${(1 - right) * -8}deg)`,
            background: `linear-gradient(150deg, ${accent}22, ${COLORS.bgCard}f5)`,
            border: `2.5px solid ${accent}`,
            borderRadius: 24,
            padding: "40px 38px",
            boxShadow: `0 0 50px ${accent}44, 0 26px 54px rgba(0,0,0,0.55)`,
          }}
        >
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 15,
              letterSpacing: 3,
              color: accent,
              marginBottom: 14,
              textShadow: glow(accent),
            }}
          >
            ✦ {slide.kicker || "EAKC"}
          </div>
          <div
            style={{
              fontSize: 30,
              fontWeight: 750,
              fontFamily: FONTS.heading,
              color: COLORS.textPrimary,
              lineHeight: 1.2,
              marginBottom: 14,
            }}
          >
            {slide.title}
          </div>
          <div
            style={{
              fontSize: 22,
              color: COLORS.textSecondary,
              lineHeight: 1.5,
              fontFamily: FONTS.body,
            }}
          >
            {slide.subtitle || slide.narration}
          </div>
        </div>
      </div>
    </SlideShell>
  );
};

const ClosingLayout: React.FC<{ slide: Slide; accent: string }> = ({
  slide,
  accent,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const a = appearAt(frame, fps, 4);
  const b = appearAt(frame, fps, 16);
  const c = appearAt(frame, fps, 30);
  return (
    <SlideShell accent={accent}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          padding: "0 180px",
        }}
      >
        <Pill
          text={slide.kicker || "Thanks for watching"}
          color={accent}
          appear={a}
          size={19}
        />
        <div
          style={{
            marginTop: 30,
            fontSize: 76,
            fontFamily: FONTS.heading,
            fontWeight: 900,
            letterSpacing: -2,
            lineHeight: 1.05,
            maxWidth: 1400,
            opacity: b,
            transform: `translateY(${(1 - b) * 28}px)`,
          }}
        >
          <Grad from="#FFFFFF" to={accent}>
            {slide.title}
          </Grad>
        </div>
        {slide.subtitle ? (
          <div
            style={{
              marginTop: 28,
              fontSize: 30,
              color: COLORS.textSecondary,
              fontFamily: FONTS.body,
              maxWidth: 1080,
              lineHeight: 1.45,
              opacity: c,
            }}
          >
            {slide.subtitle}
          </div>
        ) : null}
        <div style={{ marginTop: 44, opacity: c }}>
          <Pill
            text="EAKC · built for fun, shared now"
            color={accent}
            solid
            size={20}
          />
        </div>
      </div>
    </SlideShell>
  );
};

/* ── the dense workhorse: lede + 3-4 detail bullets + a takeaway strip ── */
const ConceptLayout: React.FC<{ slide: Slide; accent: string }> = ({
  slide,
  accent,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const a = appearAt(frame, fps, 4);
  const blob = `${slide.subtitle || ""} ${slide.narration || ""}`;
  const isIntuition = /\bintuition\b/i.test(blob);
  const theme = isIntuition ? COLORS.neonAmber : accent;
  const bullets = slide.bullets || [];
  const twoCol = bullets.length >= 4;
  const hasTake = Boolean(slide.takeaway);
  return (
    <SlideShell accent={accent}>
      <SlideChip n={slide.n} color={accent} appear={a} label={slide.kicker} />
      {isIntuition ? (
        <div
          style={{
            position: "absolute",
            right: 70,
            bottom: -30,
            fontSize: 340,
            opacity: 0.05 * a,
            filter: `drop-shadow(0 0 50px ${theme})`,
          }}
        >
          💡
        </div>
      ) : null}
      {/* header block */}
      <div
        style={{
          position: "absolute",
          top: 116,
          left: 110,
          right: 110,
          zIndex: 20,
        }}
      >
        {isIntuition ? (
          <div style={{ marginBottom: 14, opacity: a }}>
            <Pill text="✦ Intuition" color={theme} size={18} />
          </div>
        ) : null}
        <div
          style={{
            fontSize: 52,
            fontFamily: FONTS.heading,
            fontWeight: 900,
            letterSpacing: -1.2,
            lineHeight: 1.05,
            color: COLORS.textPrimary,
            textShadow: `0 0 34px ${theme}99, 0 0 80px ${theme}22`,
            opacity: appearAt(frame, fps, 8),
            transform: `translateY(${(1 - appearAt(frame, fps, 8)) * -22}px)`,
          }}
        >
          {slide.title}
        </div>
        {slide.subtitle ? (
          <div
            style={{
              marginTop: 14,
              fontSize: 27,
              color: theme,
              fontWeight: 600,
              fontFamily: FONTS.body,
              maxWidth: 1560,
              lineHeight: 1.34,
              opacity: appearAt(frame, fps, 14),
            }}
          >
            {slide.subtitle}
          </div>
        ) : null}
      </div>
      {/* detail bullets */}
      <div
        style={{
          position: "absolute",
          top: 318,
          left: 110,
          right: 110,
          bottom: hasTake ? 168 : 92,
          display: "grid",
          gridTemplateColumns: twoCol ? "1fr 1fr" : "1fr",
          gap: 16,
          alignContent: "center",
        }}
      >
        {bullets.map((b, i) => {
          const sp = appearAt(frame, fps, 22 + i * 7, SPRING.gentle);
          return (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 16,
                alignItems: "flex-start",
                background: `linear-gradient(135deg, ${theme}12, ${COLORS.bgCard}e6)`,
                border: `1.5px solid ${theme}44`,
                borderRadius: 16,
                padding: "18px 22px",
                opacity: sp,
                transform: `translateX(${(1 - sp) * -34}px)`,
                boxShadow: "0 12px 30px rgba(0,0,0,0.4)",
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: 13,
                  height: 13,
                  borderRadius: "50%",
                  marginTop: 9,
                  background: `linear-gradient(135deg, ${theme}, ${COLORS.neonCyan})`,
                  boxShadow: glow(theme),
                }}
              />
              <div>
                <div
                  style={{
                    fontSize: 25,
                    fontFamily: FONTS.heading,
                    fontWeight: 720,
                    letterSpacing: -0.3,
                    color: COLORS.textPrimary,
                  }}
                >
                  {b.head}
                </div>
                {b.sub ? (
                  <div
                    style={{
                      marginTop: 5,
                      fontSize: 19,
                      fontFamily: FONTS.body,
                      color: COLORS.textSecondary,
                      lineHeight: 1.42,
                    }}
                  >
                    {b.sub}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      {/* takeaway strip */}
      {hasTake ? (
        <div
          style={{
            position: "absolute",
            left: 110,
            right: 110,
            bottom: 60,
            opacity: appearAt(frame, fps, 26 + bullets.length * 6),
            display: "flex",
            alignItems: "center",
            gap: 16,
            background: `${theme}1a`,
            border: `1.5px solid ${theme}`,
            borderLeft: `6px solid ${theme}`,
            borderRadius: 14,
            padding: "16px 24px",
            boxShadow: `0 0 30px ${theme}33`,
          }}
        >
          <span
            style={{
              fontFamily: FONTS.mono,
              fontSize: 13,
              letterSpacing: 2.5,
              color: theme,
              fontWeight: 800,
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              textShadow: glow(theme),
            }}
          >
            Takeaway
          </span>
          <span
            style={{
              fontSize: 23,
              color: COLORS.textPrimary,
              fontFamily: FONTS.body,
              fontWeight: 500,
              lineHeight: 1.3,
            }}
          >
            {slide.takeaway}
          </span>
        </div>
      ) : null}
    </SlideShell>
  );
};

/* ───────────────────────── dispatcher ───────────────────────── */

export const SlideView: React.FC<{ slide: Slide; index: number }> = ({
  slide,
}) => {
  const accent = accentFor(slide.n);
  if (SECTION_NS.has(slide.n))
    return <SectionLayout slide={slide} accent={accent} />;
  switch (slide.layout) {
    case "title":
      return <TitleLayout slide={slide} accent={accent} />;
    case "section":
      return <SectionLayout slide={slide} accent={accent} />;
    case "quote":
      return <QuoteLayout slide={slide} accent={accent} />;
    case "concept":
      return <ConceptLayout slide={slide} accent={accent} />;
    case "bullets":
    case "timeline":
      return <BulletsLayout slide={slide} accent={accent} />;
    case "metric":
      return <MetricLayout slide={slide} accent={accent} />;
    case "compare":
      return <CompareLayout slide={slide} accent={accent} />;
    case "diagram":
      return <DiagramLayout slide={slide} accent={accent} />;
    case "split":
      return <SplitLayout slide={slide} accent={accent} />;
    case "closing":
      return <ClosingLayout slide={slide} accent={accent} />;
    case "statement":
    default:
      return <StatementLayout slide={slide} accent={accent} />;
  }
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
